import { useTranslation } from 'react-i18next';
import { Users, TrendingUp, DollarSign, Gauge, type LucideIcon } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useTenantHierarchy } from '@/hooks/useTenantHierarchy';
import { useSubtreeRollup } from '@/hooks/useSubtreeRollup';
import { isParentTenant } from '@/api/tenantHierarchy';
import {
  formatSubtreeCount,
  formatSubtreePipeline,
  formatSubtreeScore,
} from '@/api/subtreeRollup';

/**
 * Ticket 124.6c — parent-only subtree roll-up section.
 *
 * Rendered inside the TENANT_ADMIN dashboard. It self-hides unless the
 * caller is a TENANT_ADMIN whose own tenant node `hasDescendants`
 * (sourced from `/admin/tenant-hierarchy`). For a parent it shows the
 * subtree totals as metric cards plus a per-child table (one row per
 * subtree tenant) sourced from `/me/subtree-rollup`.
 */
export function SubtreeRollupSection() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const isTenantAdmin = user?.role === 'TENANT_ADMIN';

  // Only TENANT_ADMINs hit the hierarchy endpoint; others never fetch.
  const { nodes } = useTenantHierarchy(isTenantAdmin);
  const isParent = isTenantAdmin && isParentTenant(nodes, user?.tenantId);

  // Only parents fetch the roll-up.
  const { data } = useSubtreeRollup(isParent);

  // Self-hide for non-admins, leaf tenants, or before data arrives.
  if (!isParent || !data) return null;

  const { subtreeTotal, perChild } = data;

  return (
    <section className="space-y-4" data-testid="subtree-rollup-section">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {t('subtree.title')}
      </h2>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MetricCard
          icon={Users}
          label={t('subtree.leads')}
          value={formatSubtreeCount(subtreeTotal.leadsCount)}
        />
        <MetricCard
          icon={TrendingUp}
          label={t('subtree.opportunities')}
          value={formatSubtreeCount(subtreeTotal.opportunitiesCount)}
        />
        <MetricCard
          icon={DollarSign}
          label={t('subtree.pipeline_value')}
          value={formatSubtreePipeline(subtreeTotal.pipelineValue)}
        />
        <MetricCard
          icon={Gauge}
          label={t('subtree.avg_score')}
          value={formatSubtreeScore(subtreeTotal.avgSnapshotScore)}
        />
      </div>

      <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-medium">{t('subtree.col_tenant')}</th>
              <th className="px-4 py-2 text-right font-medium">{t('subtree.col_leads')}</th>
              <th className="px-4 py-2 text-right font-medium">
                {t('subtree.col_opportunities')}
              </th>
              <th className="px-4 py-2 text-right font-medium">{t('subtree.col_pipeline')}</th>
              <th className="px-4 py-2 text-right font-medium">{t('subtree.col_score')}</th>
            </tr>
          </thead>
          <tbody>
            {perChild.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                  {t('subtree.empty')}
                </td>
              </tr>
            ) : (
              perChild.map((row) => (
                <tr key={row.tenantId} className="border-b last:border-0">
                  <td className="px-4 py-2 font-medium">{row.name}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatSubtreeCount(row.leadsCount)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatSubtreeCount(row.opportunitiesCount)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatSubtreePipeline(row.pipelineValue)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatSubtreeScore(row.avgSnapshotScore)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-md border border-sidebar-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs uppercase text-muted-foreground">
        <Icon className="h-4 w-4" />
        {label}
      </div>
      <div className="mt-3 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}
